/**
 * Velocity Dashboard Component
 * Productivity metrics and performance tracking
 */

import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Progress } from '../ui/progress'
import { cn } from '../../lib/utils'
import { 
  Activity, TrendingUp, TrendingDown, Minus,
  Clock, CheckCircle, Target, Zap, BarChart3,
  ArrowUp, ArrowDown, MoreVertical, Info,
  Calendar, Timer, Flame, Award
} from 'lucide-react'

interface VelocityMetrics {
  velocityScore: number
  tasksCompleted: number
  focusTimeMinutes: number
  interruptionCount: number
  taskCompletionRate: number
  peakHours: number[]
  weeklyTrend?: {
    current: number
    previous: number
    change: number
  }
  dailyStats?: Array<{
    day: string
    completed: number
    hours: number
  }>
  achievements?: Array<{
    id: string
    title: string
    icon: string
    earnedAt: Date
  }>
}

interface VelocityDashboardProps {
  metrics: VelocityMetrics | null
  compact?: boolean
  onMetricClick?: (metric: string) => void
}

function MetricCard({
  title,
  value,
  unit,
  change,
  icon: Icon,
  color,
  onClick
}: {
  title: string
  value: number | string
  unit?: string
  change?: number
  icon: any
  color: string
  onClick?: () => void
}) {
  const getTrendIcon = () => {
    if (!change) return null
    if (change > 0) return <ArrowUp className="w-3 h-3" />
    if (change < 0) return <ArrowDown className="w-3 h-3" />
    return <Minus className="w-3 h-3" />
  }

  const getTrendColor = () => {
    if (!change) return 'text-gray-500'
    if (change > 0) return 'text-green-600'
    if (change < 0) return 'text-red-600'
    return 'text-gray-500'
  }

  return (
    <div 
      className={cn(
        "relative p-4 rounded-lg border bg-white transition-all duration-200",
        onClick && "cursor-pointer hover:shadow-md hover:border-krushr-primary/50"
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center",
          color
        )}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        {change !== undefined && (
          <div className={cn("flex items-center gap-1 text-xs", getTrendColor())}>
            {getTrendIcon()}
            <span>{Math.abs(change)}%</span>
          </div>
        )}
      </div>
      
      <p className="text-xs text-krushr-gray-500 mb-1">{title}</p>
      <p className="text-2xl font-bold text-krushr-gray-900">
        {value}
        {unit && <span className="text-sm font-normal text-krushr-gray-500 ml-1">{unit}</span>}
      </p>
    </div>
  )
}

function PeakHoursChart({ hours }: { hours: number[] }) {
  const maxValue = Math.max(...hours)
  
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-krushr-gray-700">Peak Productivity Hours</p>
      <div className="flex items-end gap-1 h-16">
        {hours.map((value, idx) => {
          const height = maxValue > 0 ? (value / maxValue) * 100 : 0
          const isPeak = value === maxValue
          
          return (
            <div key={idx} className="flex-1 flex flex-col items-center justify-end">
              <div
                className={cn(
                  "w-full rounded-t transition-all duration-300",
                  isPeak ? "bg-krushr-primary" : "bg-krushr-gray-300"
                )}
                style={{ height: `${height}%` }}
              />
              <span className="text-xs text-krushr-gray-400 mt-1">
                {idx + 9}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function VelocityScore({ score }: { score: number }) {
  const getScoreColor = () => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-blue-600'
    if (score >= 40) return 'text-orange-600'
    return 'text-red-600'
  }

  const getScoreLabel = () => {
    if (score >= 80) return 'Excellent'
    if (score >= 60) return 'Good'
    if (score >= 40) return 'Fair'
    return 'Needs Focus'
  }

  return (
    <div className="text-center">
      <div className="relative inline-flex items-center justify-center">
        <svg className="w-32 h-32 transform -rotate-90">
          <circle
            cx="64"
            cy="64"
            r="56"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-krushr-gray-200"
          />
          <circle
            cx="64"
            cy="64"
            r="56"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeDasharray={`${(score / 100) * 352} 352`}
            className={cn("transition-all duration-1000", getScoreColor())}
          />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className={cn("text-3xl font-bold", getScoreColor())}>
            {score}
          </span>
          <span className="text-xs text-krushr-gray-500">
            {getScoreLabel()}
          </span>
        </div>
      </div>
    </div>
  )
}

export default function VelocityDashboard({
  metrics,
  compact = false,
  onMetricClick
}: VelocityDashboardProps) {
  const [view, setView] = useState<'overview' | 'details'>('overview')

  if (!metrics) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-32">
          <div className="text-center">
            <Activity className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No metrics available</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (compact) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Activity className="w-4 h-4 text-krushr-primary" />
            Velocity
            <Badge variant="outline" className="ml-auto">
              {metrics.velocityScore}/100
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-krushr-gray-900">
                {metrics.tasksCompleted}
              </p>
              <p className="text-xs text-krushr-gray-500">Tasks Done</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-krushr-gray-900">
                {Math.round(metrics.focusTimeMinutes / 60)}h
              </p>
              <p className="text-xs text-krushr-gray-500">Focus Time</p>
            </div>
          </div>
          
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-krushr-gray-600">Completion Rate</span>
              <span className="font-medium">{metrics.taskCompletionRate}%</span>
            </div>
            <Progress value={metrics.taskCompletionRate} className="h-2" />
          </div>

          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full"
            onClick={() => onMetricClick?.('velocity')}
          >
            View Details
            <ArrowUp className="w-3 h-3 ml-1" />
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-krushr-gray-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-krushr-primary" />
          Velocity Dashboard
        </h3>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={view === 'overview' ? 'default' : 'ghost'}
            onClick={() => setView('overview')}
            className="h-8"
          >
            Overview
          </Button>
          <Button
            size="sm"
            variant={view === 'details' ? 'default' : 'ghost'}
            onClick={() => setView('details')}
            className="h-8"
          >
            Details
          </Button>
        </div>
      </div>

      {view === 'overview' && (
        <>
          {/* Main Score */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <VelocityScore score={metrics.velocityScore} />
                
                <div className="space-y-4">
                  <MetricCard
                    title="Tasks Completed"
                    value={metrics.tasksCompleted}
                    change={metrics.weeklyTrend?.change}
                    icon={CheckCircle}
                    color="bg-green-500"
                    onClick={() => onMetricClick?.('tasks')}
                  />
                  
                  <MetricCard
                    title="Focus Time"
                    value={Math.round(metrics.focusTimeMinutes / 60)}
                    unit="hrs"
                    icon={Clock}
                    color="bg-blue-500"
                    onClick={() => onMetricClick?.('focus')}
                  />
                </div>
                
                <div className="space-y-4">
                  <MetricCard
                    title="Completion Rate"
                    value={metrics.taskCompletionRate}
                    unit="%"
                    icon={Target}
                    color="bg-purple-500"
                    onClick={() => onMetricClick?.('completion')}
                  />
                  
                  <MetricCard
                    title="Interruptions"
                    value={metrics.interruptionCount}
                    icon={Zap}
                    color="bg-orange-500"
                    onClick={() => onMetricClick?.('interruptions')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Peak Hours */}
          {metrics.peakHours && (
            <Card>
              <CardContent className="pt-4">
                <PeakHoursChart hours={metrics.peakHours} />
              </CardContent>
            </Card>
          )}
        </>
      )}

      {view === 'details' && (
        <>
          {/* Daily Stats */}
          {metrics.dailyStats && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Daily Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {metrics.dailyStats.map((stat) => (
                    <div key={stat.day} className="flex items-center justify-between">
                      <span className="text-sm text-krushr-gray-600">{stat.day}</span>
                      <div className="flex items-center gap-4">
                        <Badge variant="outline">
                          {stat.completed} tasks
                        </Badge>
                        <Badge variant="outline">
                          {stat.hours}h
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Achievements */}
          {metrics.achievements && metrics.achievements.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent Achievements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {metrics.achievements.map((achievement) => (
                    <div 
                      key={achievement.id}
                      className="flex items-center gap-2 p-2 rounded-lg border bg-gradient-to-r from-krushr-primary/5 to-krushr-secondary/5"
                    >
                      <Award className="w-5 h-5 text-krushr-primary" />
                      <div>
                        <p className="text-xs font-medium text-krushr-gray-900">
                          {achievement.title}
                        </p>
                        <p className="text-xs text-krushr-gray-500">
                          {new Date(achievement.earnedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}